import { Loja, Venda } from '@/models'
import { ClienteGrupoEconomico } from '@/models/GrupoEconomico'
import AlertModule from '@/store/vuex/aplicacao/AlertModule'
import { consultar, ConsultaCreditoInformadoResponse } from '@/api/creditoInformado'

// Só faz a consulta e atualiza os dados do cliente (limite/bloqueio), sem abrir diálogo de
// autenticação nem mexer na venda.
export async function consultarCreditoInformadoDoCliente(venda: Venda, loja: Loja): Promise<ConsultaCreditoInformadoResponse | null> {
	if (!venda.cliente) return null

	let resposta: ConsultaCreditoInformadoResponse
	try {
		resposta = await consultar(venda.cliente.cnpjOuCpf, loja.id)
	} catch {
		return null
	}

	if (!resposta.integracaoConfigurada) return null

	// Só atualiza os campos persistidos quando a consulta respondeu com dados frescos de verdade —
	// quando consultaIndisponivel é true, bloqueado/limiteInformado/disponivelInformado são só
	// valores padrão (zerados), não o saldo real do cliente. Sobrescrever com eles zeraria o saldo
	// já persistido e faria a tela de pagamento achar que o crédito acabou sem motivo real.
	if (!resposta.consultaIndisponivel) {
		const grupos = venda.cliente.gruposEconomicos || []
		const indice = grupos.findIndex(grupo => grupo.grupoEconomico.id === loja.grupoEconomico.id)

		const grupoAtualizado: ClienteGrupoEconomico = {
			...(indice >= 0 ? grupos[indice] : {
				id: '',
				clienteId: venda.cliente.id,
				grupoEconomico: loja.grupoEconomico,
				limiteDoCrediario: 0,
				disponivelEmCrediario: 0,
			} as ClienteGrupoEconomico),
			limiteInformado: resposta.limiteInformado,
			disponivelEmLimiteInformado: resposta.disponivelInformado,
			bloqueadoCreditoInformado: resposta.bloqueado,
			motivosBloqueioCreditoInformado: resposta.motivosBloqueio.join('; '),
		}

		if (indice >= 0) {
			grupos.splice(indice, 1, grupoAtualizado)
		} else {
			grupos.push(grupoAtualizado)
		}
		venda.cliente.gruposEconomicos = grupos
	}

	return resposta
}

// Roda na busca do cliente: consulta e já avisa se estiver bloqueado/indisponível, mas não abre o
// diálogo de autenticação nem remove o cliente da venda — a autenticação só é pedida depois, quando
// o operador seleciona uma forma de pagamento que exige crédito informado (ver
// DialogoDePagamento.vue > selecionarTipoDePagamento).
export async function avisarCreditoInformadoDoCliente(venda: Venda, loja: Loja): Promise<void> {
	const resposta = await consultarCreditoInformadoDoCliente(venda, loja)
	if (!resposta) return

	if (resposta.bloqueado) {
		AlertModule.setError(`Cliente bloqueado: ${resposta.motivosBloqueio.join(', ') || 'motivo não informado'}`)
	} else if (resposta.consultaIndisponivel) {
		AlertModule.setWarning('Não foi possível confirmar o crédito do cliente agora. A consulta está indisponível.')
	}
}
